import { useState } from "react";
import { useNavigate } from "react-router";
import { login, logout, logoutFromStore, ApiError } from "@pos/shared/api";
import { useAuthStore } from "@pos/shared/stores";
import { writeAuditLog } from "@pos/shared/lib/audit";
import { TX } from "@pos/shared/types";
import type {
  LoginResponse,
  StoreOption,
  AuthUser,
  ActiveStore,
} from "@pos/shared/types";
import { APP_VERSION } from "@pos/shared";
import { LoginForm } from "@/components/login/LoginForm";
import { StoreSelectForm } from "@/components/login/StoreSelectForm";
import { StoreConfigForm } from "@/components/login/StoreConfigForm";

type LoginStep = "store-config" | "credentials" | "store-select";

export function LoginPage() {
  const navigate = useNavigate();
  const deviceId = useAuthStore((s) => s.deviceId);
  const setDeviceId = useAuthStore((s) => s.setDeviceId);
  const shopId = useAuthStore((s) => s.shopId);
  const shopName = useAuthStore((s) => s.shopName);
  const setSession = useAuthStore((s) => s.setSession);
  const setStoreConfig = useAuthStore((s) => s.setStoreConfig);

  // Show the persisted store name on the credentials screen — but only
  // when it's the real name from a prior login (or multi-store pick),
  // not the placeholder we set during initial store-config where
  // shopName === shopId.
  const storeLabel = shopName && shopName !== shopId ? shopName : null;

  const [step, setStep] = useState<LoginStep>(
    shopId ? "credentials" : "store-config",
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Multi-store temp state
  const [multiStoreTemp, setMultiStoreTemp] = useState<{
    userName: string;
    userId: string;
    stores: StoreOption[];
  } | null>(null);

  // Device conflict state
  const [deviceConflictError, setDeviceConflictError] = useState<string | null>(
    null,
  );
  const [pendingCredentials, setPendingCredentials] = useState<{
    userName: string;
    password: string;
  } | null>(null);

  // --- Store Configuration ---

  function handleStoreConfig(inputShopId: string) {
    // Generate a device ID if we don't have one
    if (!deviceId) {
      setDeviceId(crypto.randomUUID());
    }

    setStoreConfig(inputShopId, inputShopId);
    setStep("credentials");
  }

  // --- Login ---

  async function handleLogin(userName: string, password: string) {
    setIsLoading(true);
    setError(null);
    setDeviceConflictError(null);

    setPendingCredentials({ userName, password });

    try {
      const result: LoginResponse = await login({
        userName,
        password,
        deviceId,
        shopId,
        version: APP_VERSION,
      });

      handleLoginResponse(result, userName);
    } catch (err: unknown) {
      handleLoginError(err);
    } finally {
      setIsLoading(false);
    }
  }

  // --- Handle successful login response (HTTP 200/201) ---

  function handleLoginResponse(result: LoginResponse, userName: string) {
    const { data } = result;

    // Multi-store: show store selection
    if ("multipleStores" in data && data.multipleStores) {
      setMultiStoreTemp({
        userName,
        userId: data.userId,
        stores: data.stores,
      });
      setStep("store-select");
      return;
    }

    // Single-store success
    if ("token" in data) {
      const user: AuthUser = {
        userId: data.userId,
        userName: data.userName,
        userType: data.userType,
      };
      const store: ActiveStore = {
        shopId: data.shopId,
        shopName: data.storeName,
      };

      setSession(data.token, user, store);
      writeAuditLog(1, "S", TX.LOGIN, "Login", `Logged in as ${user.userName}`);
      navigate("/", { replace: true });
    }
  }

  // --- Handle login errors (HTTP 4xx/5xx) ---

  function handleLoginError(err: unknown) {
    if (err instanceof ApiError) {
      const body =
        err.body && typeof err.body === "object"
          ? (err.body as { deviceId?: string; message?: string })
          : null;
      // 409 Conflict — device conflict (server returns `deviceId` in
      // body so the user can force-login from the other device).
      if (err.status === 409 || body?.deviceId) {
        setDeviceConflictError(
          body?.message || "User connected with other device",
        );
        return;
      }
      setError(
        body?.message || err.message || "Login failed. Please try again.",
      );
      return;
    }
    // Network error (fetch threw before we got a response).
    setError("Unable to connect to the server. Please try again.");
  }

  // --- Multi-Store Login ---

  async function handleStoreSelect(selectedShopId: string, password: string) {
    if (!multiStoreTemp) return;

    setIsLoading(true);
    setError(null);
    setDeviceConflictError(null);

    const selectedStore = multiStoreTemp.stores.find(
      (s) => s.shopId === selectedShopId,
    );

    setStoreConfig(selectedShopId, selectedStore?.storeName ?? selectedShopId);

    try {
      const result: LoginResponse = await login({
        userName: multiStoreTemp.userName,
        password,
        deviceId,
        shopId: selectedShopId,
        version: APP_VERSION,
        skipStoreSelection: true,
      });

      handleLoginResponse(result, multiStoreTemp.userName);
    } catch (err: unknown) {
      handleLoginError(err);
    } finally {
      setIsLoading(false);
    }
  }

  async function handleStoreSelectBack() {
    if (multiStoreTemp) {
      try {
        await logout();
      } catch {
        // Network errors are acceptable -- still go back
      }
    }
    setMultiStoreTemp(null);
    setError(null);
    setDeviceConflictError(null);
    setStep("credentials");
  }

  // --- Force Login ---

  async function handleForceLogin() {
    if (!pendingCredentials) return;

    setIsLoading(true);
    setDeviceConflictError(null);
    setError(null);

    try {
      await logoutFromStore(shopId, pendingCredentials.userName);

      const result: LoginResponse = await login({
        userName: pendingCredentials.userName,
        password: pendingCredentials.password,
        deviceId,
        shopId,
        version: APP_VERSION,
        skipStoreSelection: true,
      });

      handleLoginResponse(result, pendingCredentials.userName);
    } catch (err: unknown) {
      handleLoginError(err);
    } finally {
      setIsLoading(false);
    }
  }

  // --- Change Store ---

  function handleChangeStore() {
    setStoreConfig("", "");
    setError(null);
    setDeviceConflictError(null);
    setStep("store-config");
  }

  // --- Render ---

  if (step === "store-config") {
    return (
      <StoreConfigForm
        onSubmit={handleStoreConfig}
        isLoading={isLoading}
        error={error}
      />
    );
  }

  if (step === "store-select" && multiStoreTemp) {
    return (
      <StoreSelectForm
        stores={multiStoreTemp.stores}
        onSubmit={handleStoreSelect}
        onBack={handleStoreSelectBack}
        isLoading={isLoading}
        error={error}
        deviceConflictError={deviceConflictError}
        onForceLogin={deviceConflictError ? handleForceLogin : null}
      />
    );
  }

  return (
    <LoginForm
      onSubmit={handleLogin}
      isLoading={isLoading}
      error={error}
      deviceConflictError={deviceConflictError}
      onForceLogin={deviceConflictError ? handleForceLogin : null}
      onChangeDevice={handleChangeStore}
      storeLabel={storeLabel}
    />
  );
}
